import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/UI/primitives/Button';
import { useUser } from '../contexts/UserContext';
import {
  buildResilienceCircleSourceKey,
  persistRcProspectKickoff,
  RC_HEADLINE,
  RC_HEADLINE_SECONDARY,
  RC_SITUATIONS,
} from '../content/resilienceCircle/landingModel';
import { buildProspectorEntryPath, persistLandingSourceKey } from '../utils/landingSourceKey';
import styles from './ResilienceCircleLandingPage.module.css';

/**
 * Entry for Resilience Circle prospects.
 * Hands prospect code RC to the prospect agent. Account creation collects name and email.
 */
export const ResilienceCircleLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useUser();

  const continueToProspectAgent = () => {
    const sourceKey = buildResilienceCircleSourceKey();
    persistLandingSourceKey(sourceKey);
    persistRcProspectKickoff();
    const prospectorPath = buildProspectorEntryPath(sourceKey);
    if (isAuthenticated) {
      navigate(prospectorPath);
      return;
    }
    navigate(`/register?next=${encodeURIComponent(prospectorPath)}`);
  };

  return (
    <main className={styles.landing} data-testid="rc-landing">
      <header className={styles.nav}>
        <p className={styles.brand}>Resilience Circle</p>
        <button type="button" className={styles.signInBtn} onClick={() => navigate('/login')}>
          Sign in
        </button>
      </header>

      <section className={styles.hero} aria-labelledby="rc-hero-heading">
        <div className={styles.heroInner}>
          <p className={styles.kicker}>founder group · prospect agent</p>
          <h1 id="rc-hero-heading" className={styles.h1}>
            {RC_HEADLINE}
            <br />
            <span className={styles.h1Secondary}>{RC_HEADLINE_SECONDARY}</span>
          </h1>
          <p className={styles.heroSub}>
            For founders and owner-operators carrying the company without an installed system.
            Create your account, then the prospect agent asks a few questions, matches you to the
            Resilience Circle ICP list, and opens the assessment and profile that fit.
          </p>
          <ul className={styles.cardGrid}>
            {RC_SITUATIONS.map((situation) => (
              <li key={situation.id} className={styles.card}>
                <h2 className={styles.cardTitle}>{situation.title}</h2>
                <p className={styles.cardBody}>{situation.summary}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={styles.band} aria-labelledby="rc-magnet-heading">
        <div className={styles.bandInner}>
          <h2 id="rc-magnet-heading" className={styles.h2}>
            Continue with the prospect agent
          </h2>
          <p className={styles.bandBody}>
            Account creation is the first step. After that you answer in the conversation. The
            agent uses prospect code RC and does not assume which profile you are.
          </p>
          <Button
            type="button"
            variant="primary"
            size="lg"
            iconRight={<ArrowRight size={18} aria-hidden />}
            onClick={continueToProspectAgent}
          >
            Talk with the prospect agent
          </Button>
        </div>
      </section>

      <footer className={styles.footer}>
        <p className={styles.footerCopy}>
          Resilience Circle — the prospect agent matches you to the right assessment and profile.
        </p>
      </footer>
    </main>
  );
};
